import React, { createContext, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from '@sentry/react';
import classNames from 'classnames';
import Layout from '~/components/layout/Layout';
import InstantSearch from '~/components/organisms/instantSearch/instantSearch';
import TabNavigation from '~/components/organisms/tabs/tabs';
import CommonTemplate from '~/components/templates/commonTemplate';
import { useConfig } from '~/core/configuration/configContext';
import { usePersistence } from '~/core/persistence/PersistenceContext';
import { useSearchQuery } from '~/core/state/searchQueryContext';
import { useSERPState } from '~/core/state/SERPStateContext';
import { LogoClickProperties } from '~/hooks/useLogoClickHandler';
import { CloseSERP } from '~/hooks/useShowSearch';
import { useDynamicComponents } from '~/plugins/dynamicComponents/dynamicComponentsContext';
import { getEmptyProperties } from '~/utils/objectEmpty';
import { ResultsErrorMessage } from './errors/ResultsErrorMessage';
import layoutStyles from './layout/layout.modules.scss';

export interface IMainComponentProps {
    innerSERPRef: React.RefObject<HTMLDivElement>;
    closeSERP: CloseSERP;
    /** Contains the logo click handler and isLogoClickable flag */
    logoClickProperties: LogoClickProperties;
    productsTitleRef: React.RefObject<HTMLDivElement>;
    isInstantSearch: boolean;
    setInstantSearch: (instantSearch: boolean) => void;
    hideBodyOverflow: () => void;
}

export const FilterVisibilityContext = createContext({
    isLayoutOverflowHidden: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setIsLayoutOverflowHidden: (visible: boolean) => {},
});

const Main: React.FC<IMainComponentProps> = ({
    innerSERPRef,
    closeSERP,
    logoClickProperties,
    productsTitleRef,
    isInstantSearch,
    setInstantSearch,
    hideBodyOverflow,
}) => {
    const config = useConfig();
    const persistence = usePersistence();
    const searchState = useSERPState();
    const { searchQuery, updateSearchQuery } = useSearchQuery();

    const { Banners, SearchOverview } = useDynamicComponents();
    const [autofocus] = useState(!isInstantSearch);

    const { fulltextSets, productSet, categorySet, hasFulltextResults } = React.useMemo(() => {
        const fulltextSets = searchState.sets.filter((set) => set.layoutPosition.includes('serp-fulltext'));
        const productSet = searchState.sets.find((set) => set.layoutPosition === 'serp-products');
        const categorySet = searchState.sets.filter((set) => set.layoutPosition.includes('serp-categories'));

        const hasFulltextResults = fulltextSets.some((set) => {
            const setResults = searchState.results.sets.find(
                (result) => result.querySetIdentifier === set.querySet.identifier
            );
            return setResults && setResults.hits.length > 0;
        });

        return { fulltextSets, productSet, categorySet, hasFulltextResults };
    }, [searchState.results, searchState.sets]);

    const showSearchOverview = searchState.loadingFinished && searchQuery.length > 1 && searchQuery.length > 1;
    const totalRecords = searchState.results.filteredCategoriesRecordsCount;
    // TODO: consider moving to hook - it is used across aplication
    const isFulltextOnly = undefined === productSet && categorySet.length === 0;

    const [isLayoutOverflowHidden, setIsLayoutOverflowHidden] = useState(false);

    // used for transitioning from instant search to full SERP
    const openFullSERP = useCallback(() => {
        persistence.updateURLQuery(searchQuery);
        hideBodyOverflow();
        setInstantSearch(false);
    }, [searchQuery]);

    useEffect(() => {
        if (persistence.getSearchQueryFromURL() !== '' && isInstantSearch) {
            openFullSERP();
        }
    }, []);

    if (isInstantSearch) {
        return <InstantSearch openFullSERP={openFullSERP} closeSERP={closeSERP} innerSERPRef={innerSERPRef} />;
    }

    return (
        <FilterVisibilityContext.Provider value={{ isLayoutOverflowHidden, setIsLayoutOverflowHidden }}>
            <Layout
                logoURL={config.layout.configuration.logoUrl}
                logoClickProperties={logoClickProperties}
                showSERPContent={showSearchOverview}
                searchQuery={searchQuery}
                updateSearchQuery={updateSearchQuery}
                closeSERP={closeSERP}
                innerSERPRef={innerSERPRef}
                autofocus={autofocus}
            >
                <ErrorBoundary
                    fallback={<ResultsErrorMessage />}
                    beforeCapture={(scope) => {
                        scope.setExtra('query', `"${searchQuery}"`);
                        scope.setExtra('error', `${searchState.error}`);
                        scope.setExtra('emptyFields', `${getEmptyProperties(searchState).length}`);
                        scope.addAttachment({ filename: 'response.json', data: JSON.stringify(searchState) });
                    }}
                >
                    <TabNavigation />
                    {/* {showSearchOverview ? (
                            <SearchOverview
                                searchQuery={searchQuery}
                                totalRecords={totalRecords}
                                isFulltextOnly={isFulltextOnly}
                                containsCollapsed={resultsContainCollapsedItems(
                                    searchState.results.sets.find(
                                        (set) => set.querySetIdentifier === productSet?.querySet.identifier
                                    ) as ProductResults_State
                                )}
                            />
                        ) : null} */}
                    {searchState.loadingFinished ? (
                        <div
                            className={classNames(layoutStyles.serpContent, {
                                [layoutStyles.withRows]: searchState.resultLayout === 'rows',
                            })}
                        >
                            <Banners />
                            <CommonTemplate
                                hasFulltextResults={hasFulltextResults}
                                showSearchOverview={showSearchOverview}
                                fulltextSets={fulltextSets}
                                categorySet={categorySet}
                                productSet={productSet}
                                productsTitleRef={productsTitleRef}
                            />
                        </div>
                    ) : null}
                </ErrorBoundary>
            </Layout>
        </FilterVisibilityContext.Provider>
    );
};

export default Main;
